import React, { useEffect, useState, useCallback } from 'react';
import { api, fmtMoney, formatApiError } from '../../lib/api';
import { useDarkMode } from '../../context/DarkModeContext';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { Plus, Trash2, Pencil, Settings2, GripVertical, X } from 'lucide-react';

const Pipeline = ({ lob }) => {
  const darkMode = useDarkMode();
  const { user } = useAuth();
  const { reloadLobs } = useData();
  const aBg = darkMode ? lob?.accentBgDark : lob?.accentBg;
  const aTc = darkMode ? lob?.accentTcDark : lob?.accentTc;
  const [deals, setDeals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [err, setErr] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [editDeal, setEditDeal] = useState(null);
  const [showStages, setShowStages] = useState(false);

  const load = useCallback(() => {
    if (!lob) return;
    Promise.all([
      api.get(`/deals?lob_id=${lob.id}`).then((r) => r.data),
      api.get(`/contacts?lob_id=${lob.id}`).then((r) => r.data),
    ]).then(([d, c]) => { setDeals(d); setContacts(c); }).catch((e) => setErr(formatApiError(e)));
  }, [lob?.id]);
  useEffect(() => { load(); }, [load]);

  const contactName = (id) => {
    const c = contacts.find((x) => x.id === id);
    return c ? `${c.first} ${c.last}` : null;
  };

  const onDrop = async (stageKey) => {
    if (!dragId) return;
    const cur = deals.find((d) => d.id === dragId);
    setDragOver(null); setDragId(null);
    if (!cur || cur.stage === stageKey) return;
    setDeals((prev) => prev.map((d) => (d.id === dragId ? { ...d, stage: stageKey } : d)));
    try { await api.patch(`/deals/${dragId}`, { stage: stageKey }); }
    catch (e) { setErr(formatApiError(e)); load(); }
  };

  const removeDeal = async (id) => {
    if (!window.confirm('Delete this deal?')) return;
    try { await api.delete(`/deals/${id}`); load(); }
    catch (e) { setErr(formatApiError(e)); }
  };

  return (
    <>
      <div className="page-toolbar" data-testid="pipeline-toolbar">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <span className="page-title">Pipeline</span>
          <span className="lob-badge" style={{ background: aBg }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: lob.accent, display: 'inline-block', marginRight: 4 }} />
            <span style={{ color: aTc }}>{lob.label}</span>
          </span>
        </div>
        {user?.role === 'super_admin' && (
          <button className="dp-btn" onClick={() => setShowStages(true)} data-testid="edit-stages-btn">
            <Settings2 size={11} style={{ marginRight: 4 }} />Edit stages
          </button>
        )}
        <button className="cta-btn" style={{ background: lob.accent }} onClick={() => setShowCreate(true)} data-testid="add-deal-pipeline-btn">
          <Plus size={11} style={{ marginRight: 4 }} />Add deal
        </button>
      </div>

      {err && <div className="module-banner err" data-testid="pipeline-error">{err}</div>}

      <div className="kanban-board" data-testid="kanban-board">
        {(lob.pipelineStages || []).map((s) => {
          const stageDeals = deals.filter((d) => d.stage === s.key);
          const total = stageDeals.reduce((sum, d) => sum + (d.amount || 0), 0);
          return (
            <div className="kanban-col" key={s.key} data-testid={`kanban-col-${s.key}`}>
              <div className="col-header">
                <div className="col-stage">
                  <div className="col-dot" style={{ background: lob.accent }} />
                  {s.label}
                </div>
                <div className="col-prob">{s.prob}% probability</div>
                <div className="col-totals">
                  <span className="col-total">{fmtMoney(total)}</span>
                  <span className="col-cnt">{stageDeals.length} deal{stageDeals.length !== 1 ? 's' : ''}</span>
                </div>
              </div>
              <div className={`drop-zone${dragOver === s.key ? ' drag-over' : ''}`}
                   onDragOver={(e) => { e.preventDefault(); setDragOver(s.key); }}
                   onDragLeave={() => setDragOver(null)}
                   onDrop={() => onDrop(s.key)}
                   data-testid={`drop-zone-${s.key}`}>
                {stageDeals.map((d) => (
                  <div key={d.id} className="deal-card" draggable
                       onDragStart={() => setDragId(d.id)}
                       onDragEnd={() => { setDragId(null); setDragOver(null); }}
                       data-testid={`deal-card-${d.id}`}>
                    <div className="dc-name">
                      {d.name}
                      {d.lost && <span className="tag-pill" style={{ background: 'var(--red-msg)', color: 'var(--error)', marginLeft: 6, fontSize: 9 }} data-testid={`deal-lost-badge-${d.id}`}>Lost</span>}
                    </div>
                    <div className="dc-amt">{fmtMoney(d.amount)}</div>
                    {d.type && (
                      <span
                        className="spill"
                        style={
                          d.type === 'Loan Quotes'
                            ? { background: 'var(--orange-light)', color: darkMode ? '#ff9200' : '#7a3c00' }
                            : { background: aBg, color: aTc }
                        }
                        data-testid={`deal-type-badge-${d.id}`}
                      >
                        {d.type}
                      </span>
                    )}
                    {d.contact_id && contactName(d.contact_id) && (
                      <div style={{ fontSize: 10, color: 'var(--text-faded)', marginTop: 3 }} data-testid={`deal-contact-link-${d.id}`}>
                        → {contactName(d.contact_id)}
                      </div>
                    )}
                    <div className="dc-footer">
                      <span className="dc-owner">{d.close_date || ''}</span>
                      <span style={{ display: 'flex', gap: 4 }}>
                        <button className="btn-link" style={{ color: 'var(--text-faded)', padding: 0 }} onClick={() => setEditDeal(d)} data-testid={`edit-deal-${d.id}`}><Pencil size={11} /></button>
                        <button className="btn-link" style={{ color: 'var(--text-faded)', padding: 0 }} onClick={() => removeDeal(d.id)} data-testid={`del-deal-${d.id}`}><Trash2 size={11} /></button>
                      </span>
                    </div>
                  </div>
                ))}
                {stageDeals.length === 0 && <div style={{ padding: 14, textAlign: 'center', fontSize: 10, color: 'var(--text-soft)' }}>Drop deals here</div>}
              </div>
            </div>
          );
        })}
      </div>

      {(showCreate || editDeal) && (
        <DealModal lob={lob} deal={editDeal} contacts={contacts} onClose={(saved) => { setShowCreate(false); setEditDeal(null); if (saved) load(); }} />
      )}
      {showStages && (
        <StageEditorModal lob={lob} onClose={(saved) => { setShowStages(false); if (saved) reloadLobs(); }} />
      )}
    </>
  );
};

const DealModal = ({ lob, deal, contacts, onClose }) => {
  const stages = lob.pipelineStages || [];
  const [form, setForm] = useState(deal ? {
    ...deal, amount: deal.amount ?? '', contact_id: deal.contact_id || '', lost_reason: deal.lost_reason || '',
  } : { lob_id: lob.id, name: '', amount: '', stage: stages[0]?.key || '', type: lob.dealTypes?.[0] || '', close_date: '', contact_id: '', lost: false, lost_reason: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr('');
    const payload = {
      name: form.name, amount: Number(form.amount) || 0, stage: form.stage, type: form.type,
      close_date: form.close_date || null, contact_id: form.contact_id || null,
      lost: !!form.lost, lost_reason: form.lost ? (form.lost_reason || null) : null,
    };
    try {
      if (deal) await api.patch(`/deals/${deal.id}`, payload);
      else await api.post('/deals', { ...payload, lob_id: lob.id });
      onClose(true);
    } catch (ex) { setErr(formatApiError(ex)); } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay-v2" onClick={() => onClose(false)}>
      <form className="modal-card" onClick={(e) => e.stopPropagation()} onSubmit={submit} data-testid="deal-modal">
        <div className="modal-title">{deal ? 'Edit deal' : 'New deal'}</div>
        <label className="f-label">Name</label>
        <input className="f-input" value={form.name} onChange={(e) => set('name', e.target.value)} required data-testid="dm-name" />
        <div className="modal-row">
          <div><label className="f-label">Amount ($)</label><input className="f-input" type="number" step="0.01" value={form.amount} onChange={(e) => set('amount', e.target.value)} data-testid="dm-amount" /></div>
          <div><label className="f-label">Stage</label>
            <select className="f-select" value={form.stage} onChange={(e) => set('stage', e.target.value)} data-testid="dm-stage">
              {stages.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
        </div>
        <div className="modal-row">
          <div><label className="f-label">Type</label>
            <select className="f-select" value={form.type} onChange={(e) => set('type', e.target.value)} data-testid="dm-type">
              {(lob.dealTypes || []).map((t) => <option key={t} value={t}>{t}</option>)}
              <option value="">— Other</option>
            </select>
          </div>
          <div><label className="f-label">Close date</label><input className="f-input" type="date" value={form.close_date || ''} onChange={(e) => set('close_date', e.target.value)} data-testid="dm-close" /></div>
        </div>
        <label className="f-label">Link to contact</label>
        <select className="f-select" value={form.contact_id || ''} onChange={(e) => set('contact_id', e.target.value)} data-testid="dm-contact">
          <option value="">— No contact linked —</option>
          {contacts.map((c) => <option key={c.id} value={c.id}>{c.first} {c.last}{c.company ? ` (${c.company})` : ''}</option>)}
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, marginTop: 12 }}>
          <input type="checkbox" checked={!!form.lost} onChange={(e) => set('lost', e.target.checked)} data-testid="dm-lost-toggle" />
          Mark this deal as lost
        </label>
        {form.lost && (
          <input className="f-input" style={{ marginTop: 6 }} placeholder="Reason (optional)" value={form.lost_reason || ''} onChange={(e) => set('lost_reason', e.target.value)} data-testid="dm-lost-reason" />
        )}
        {err && <div className="f-error" data-testid="dm-error">{err}</div>}
        <div className="modal-actions">
          <button type="button" className="btn-secondary" onClick={() => onClose(false)} data-testid="dm-cancel">Cancel</button>
          <button type="submit" className="btn-primary" disabled={busy} data-testid="dm-submit">{busy ? 'Saving…' : (deal ? 'Save' : 'Create')}</button>
        </div>
      </form>
    </div>
  );
};

const StageEditorModal = ({ lob, onClose }) => {
  const [stages, setStages] = useState((lob.pipelineStages || []).map((s) => ({ ...s })));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const addStage = () => setStages((s) => [...s, { key: `stage_${Date.now().toString(36)}`, label: 'New Stage', prob: 0 }]);
  const updateStage = (idx, patch) => setStages((s) => s.map((st, i) => (i === idx ? { ...st, ...patch } : st)));
  const removeStage = (idx) => { if (window.confirm("Remove this stage? Deals currently in it will keep their data but won't show in the kanban until moved.")) setStages((s) => s.filter((_, i) => i !== idx)); };
  const move = (idx, dir) => setStages((s) => {
    const arr = [...s]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return s;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return arr;
  });

  const save = async () => {
    if (stages.length === 0) { setErr('Add at least one stage.'); return; }
    setBusy(true); setErr('');
    try {
      const clean = stages.map((s) => ({ key: s.key, label: s.label, prob: Number(s.prob) || 0 }));
      await api.patch(`/lobs/${lob.id}`, { pipeline_stages: clean });
      onClose(true);
    } catch (e) { setErr(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="modal-overlay-v2" onClick={() => onClose(false)}>
      <div className="modal-card" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()} data-testid="stage-editor-modal">
        <div className="modal-title">Edit pipeline stages — {lob.label}</div>
        {stages.map((s, idx) => (
          <div key={s.key} className="card" style={{ marginBottom: 8, padding: 10 }} data-testid={`stage-row-${idx}`}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <GripVertical size={12} style={{ opacity: 0.4, flexShrink: 0 }} />
              <input className="f-input" style={{ flex: 1.6 }} value={s.label} onChange={(e) => updateStage(idx, { label: e.target.value })} placeholder="Stage label" data-testid={`stage-label-${idx}`} />
              <input className="f-input" type="number" min="0" max="100" style={{ width: 70 }} value={s.prob} onChange={(e) => updateStage(idx, { prob: e.target.value })} data-testid={`stage-prob-${idx}`} />
              <button type="button" className="dp-btn" style={{ padding: '4px 7px' }} onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`stage-up-${idx}`}>↑</button>
              <button type="button" className="dp-btn" style={{ padding: '4px 7px' }} onClick={() => move(idx, 1)} disabled={idx === stages.length - 1} data-testid={`stage-down-${idx}`}>↓</button>
              <button type="button" className="dp-btn" style={{ padding: '4px 7px' }} onClick={() => removeStage(idx)} data-testid={`stage-remove-${idx}`}><X size={12} /></button>
            </div>
          </div>
        ))}
        <button type="button" className="dp-btn" style={{ width: '100%', justifyContent: 'center', marginTop: 4 }} onClick={addStage} data-testid="stage-add-btn">
          <Plus size={11} style={{ marginRight: 4 }} />Add stage
        </button>
        {err && <div className="f-error" data-testid="stage-editor-error">{err}</div>}
        <div className="modal-actions" style={{ marginTop: 14 }}>
          <button type="button" className="btn-secondary" onClick={() => onClose(false)} data-testid="stage-editor-cancel">Cancel</button>
          <button type="button" className="btn-primary" onClick={save} disabled={busy} data-testid="stage-editor-save">{busy ? 'Saving…' : 'Save stages'}</button>
        </div>
      </div>
    </div>
  );
};

export default Pipeline;
